(function () {
  const SELECTOR = '[data-careers-spirit-values]';

  class CareersSpiritValues {
    constructor(section) {
      this.section = section;
      this.pin = section.querySelector('.CareersSpiritValues__pin');
      this.items = Array.from(section.querySelectorAll('[data-csv-item]'));
      this.tokens = [];
      this.count = 0;
      this.activeTokenIndex = -2;
      this.raf = null;
      this.scrollTrigger = null;

      if (!this.pin || !this.items.length) return;

      this.init();
    }

    init() {
      this.prepareTokens();
      this.setState(-1);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setState(this.tokens.length - 1);
        return;
      }

      if (window.gsap && window.ScrollTrigger) {
        this.initGsap();
      } else {
        this.initNative();
      }
    }

    prepareTokens() {
      this.tokens = [];

      this.items.forEach((item, itemIndex) => {
        const numberEl = item.querySelector('[data-csv-number]');
        const statementEl = item.querySelector('[data-csv-statement]');

        const fullText = item.textContent.trim().replace(/\s+/g, ' ');
        item.setAttribute('aria-label', fullText);

        if (numberEl) {
          this.wrapElementText(numberEl, itemIndex, true);
        }

        if (statementEl) {
          this.wrapElementText(statementEl, itemIndex, false);
        }
      });

      this.count = this.tokens.length;
    }

    wrapElementText(element, itemIndex, isNumber) {
      const originalText = element.textContent.trim();

      if (!originalText) return;

      element.innerHTML = '';

      const parts = originalText.split(/(\s+)/);

      parts.forEach((part) => {
        if (part.trim() === '') {
          element.appendChild(document.createTextNode(part));
          return;
        }

        const token = document.createElement('span');

        token.className = isNumber
          ? 'CareersSpiritValues__token CareersSpiritValues__token--number'
          : 'CareersSpiritValues__token CareersSpiritValues__token--word';

        token.textContent = part;
        token.dataset.csvToken = '';
        token.dataset.itemIndex = String(itemIndex);

        element.appendChild(token);
        this.tokens.push(token);
      });
    }

    initGsap() {
      window.gsap.registerPlugin(window.ScrollTrigger);

      const scrollDistance = Math.max(window.innerHeight * this.items.length, 1);

      this.scrollTrigger = window.ScrollTrigger.create({
        trigger: this.section,
        start: 'top top',
        end: `+=${scrollDistance}`,
        pin: this.pin,
        scrub: 0.5,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          this.updateByProgress(self.progress);
        },
        onLeaveBack: () => {
          this.setState(-1);
        }
      });
    }

    initNative() {
      this.section.classList.add('CareersSpiritValues--nativePin');

      this.onScroll = this.requestUpdate.bind(this);
      this.onResize = this.requestUpdate.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.requestUpdate();
    }

    requestUpdate() {
      if (this.raf) return;

      this.raf = window.requestAnimationFrame(() => {
        this.raf = null;
        this.updateNative();
      });
    }

    updateNative() {
      const rect = this.section.getBoundingClientRect();
      const scrollable = Math.max(this.section.offsetHeight - window.innerHeight, 1);
      const travelled = Math.min(Math.max(-rect.top, 0), scrollable);
      const progress = travelled / scrollable;

      if (rect.top > 0) {
        this.setState(-1);
        return;
      }

      this.updateByProgress(progress);
    }

    updateByProgress(progress) {
      const startOffset = 0.035;

      if (progress <= startOffset) {
        this.setState(-1);
        return;
      }

      const normalized = Math.min(
        Math.max((progress - startOffset) / (1 - startOffset), 0),
        1
      );

      const tokenIndex = Math.min(
        Math.floor(normalized * this.count),
        this.count - 1
      );

      this.setState(tokenIndex);
    }

    setState(activeTokenIndex) {
      if (this.activeTokenIndex === activeTokenIndex) return;

      this.activeTokenIndex = activeTokenIndex;

      this.items.forEach((item) => {
        item.classList.remove('is-active', 'is-visible');
        item.removeAttribute('aria-current');
      });

      this.tokens.forEach((token, tokenIndex) => {
        const itemIndex = Number(token.dataset.itemIndex);
        const isRevealed = activeTokenIndex >= 0 && tokenIndex <= activeTokenIndex;

        token.classList.remove('is-active', 'is-visible');

        if (!isRevealed) return;

        if (itemIndex === 0) {
          token.classList.add('is-active');
          this.items[itemIndex].setAttribute('aria-current', 'true');
        } else {
          token.classList.add('is-visible');
        }
      });
    }

    destroy() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
      }

      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
      }

      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
      }

      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
      }
    }
  }

  const instances = new WeakMap();

  function initSections(container = document) {
    container.querySelectorAll(SELECTOR).forEach((section) => {
      if (instances.has(section)) return;

      instances.set(section, new CareersSpiritValues(section));
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initSections();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SELECTOR);

    if (!section || !instances.has(section)) return;

    instances.get(section).destroy();
    instances.delete(section);
  });
})();